
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import { Link } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import contaService from '../../services/contaService';

// Os dados agora vêm do serviço contaService

const columns = [
  { Header: 'ID', accessor: 'id' },
  { Header: 'Código', accessor: 'codigo' },
  { Header: 'Descrição', accessor: 'descricao' },
  { Header: 'Tipo', accessor: 'tipo' },
  { Header: 'Contabilizar', accessor: 'contabilizar' },
  { Header: 'Data Inclusão', accessor: 'dataInclusao', Cell: (value) => value ? new Date(value).toLocaleDateString() : '' },
  { Header: 'Data Alteração', accessor: 'dataAlteracao', Cell: (value) => value ? new Date(value).toLocaleDateString() : '' },
];

const pageSizes = [5, 10, 20];

const ContasList = () => {
  const [filters, setFilters] = useState({ descricao: '', tipo: '', codigo: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columns.map(col => col.accessor));

  // Estado para contas
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    const loadContas = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await contaService.list({ page, limit: pageSize, ...filters });
        setContas(response.contas || []);
        setPagination({
          total: response.total,
          totalPages: response.totalPages,
          page: response.page,
          limit: response.limit
        });
      } catch (error) {
        console.error('Erro ao carregar contas:', error);
        setError('Erro ao carregar contas');
      } finally {
        setLoading(false);
      }
    };

    loadContas();
  }, [page, pageSize, filters]);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Filtragem (removida pois agora é feita no backend)
  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Contas</h2>
            <Link
              to="/contas/create"
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Nova Conta</span>
            </Link>
          </div>
          <ColumnVisibilityModal
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />
          <div className="max-w-full mb-4">
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                name="descricao"
                value={filters.descricao}
                onChange={handleFilterChange}
                placeholder="Descrição"
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <select
                name="tipo"
                value={filters.tipo}
                onChange={handleFilterChange}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">Todos os tipos</option>
                <option value="Entrada">Entrada</option>
                <option value="Saida">Saida</option>
              </select>
              <input
                type="text"
                name="codigo"
                value={filters.codigo}
                onChange={handleFilterChange}
                placeholder="Código"
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination 
                currentPage={page} 
                totalPages={pagination.totalPages || 1}
                totalItems={pagination.total || 0}
                itemsPerPage={pageSize}
                onPageChange={handlePageChange}
              />
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          <div className="max-w-full">
            <Table
              columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                if (col.accessor === 'descricao') {
                  return {
                    ...col,
                    Cell: (value, row) => (
                      <div className="flex items-center gap-2">
                        <span>{value}</span>
                        <Link
                          to={`/contas/${row.id}/edit`}
                          className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Link>
                      </div>
                    )
                  };
                }
                return col;
              })}
              data={contas}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContasList;
